'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import dynamic from 'next/dynamic';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaFullscreenIcon} from '../icon';
import {useDirectionalScroller} from '../listing/useDirectionalScroller';
import {Button, FullscreenButton, NavArrow, SectionTitle, SoundButton} from '../primitives';
import {GalleryCarousel} from './GalleryCarousel';
import {HeroGallerySlide} from './HeroGallery/HeroGallerySlide';
import {MediaType, type MediaAsset} from './types';

// Code-split the full-screen lightbox — only fetched when a viewer opens it.
const GalleryModal = dynamic(() => import('./GalleryModal').then(m => m.GalleryModal), {ssr: false});

export interface MediaGalleryProps {
  assets: MediaAsset[];
  title?: string;
  modalTitle?: string;
  showViewAll?: boolean;
  onOpen?: () => void;
  layout?: 'auto' | 'carousel';
  singleFrame?: 'banner' | 'media';
  showFullscreen?: boolean;
}

const Header = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// [MASTER] Carousel Gallery Section: the arrows sit at the end of the header,
// after the view-all button, and only on desktop.
const HeaderEnd = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const HeaderArrows = styled(Box)(({theme}) => ({
  display: 'none',
  gap: theme.spacing(1),
  [theme.breakpoints.up('md')]: {display: 'flex'},
}));

// Single-asset layout (Figma "Masterplan", node 1124:30399): one full-width
// frame with a corner control that opens the lightbox. `banner` crops to a
// fixed height (a masterplan map); `media` keeps a 16:9 box so the asset shows
// whole (a news article's main video/image).
const SingleFrame = styled(Box, {shouldForwardProp: propName => propName !== '$media'})<{$media?: boolean}>(({theme, $media}) => ({
  position: 'relative',
  width: '100%',
  ...($media ? {aspectRatio: '16 / 9'} : {height: 280, [theme.breakpoints.up('md')]: {height: 500}}),
  borderRadius: theme.radii.images,
  border: `1px solid ${theme.palette.colors.border.neutral.subtle}`,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  cursor: 'pointer',
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
}));

// `position: relative` keeps the shared FullscreenButton painted above the
// absolutely-positioned media element it sits beside (see SoundButton).
const fullscreenButtonSx = {position: 'relative' as const};

export function MediaGallery({
  assets,
  title,
  modalTitle,
  showViewAll = false,
  onOpen,
  layout = 'auto',
  singleFrame = 'banner',
  showFullscreen = true,
}: MediaGalleryProps) {
  const {t} = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const {ref, canPrev, canNext, scrollByDir} = useDirectionalScroller<HTMLDivElement>(assets.length);
  const lightboxTitle = modalTitle ?? title;

  if (assets.length === 0) {
    return null;
  }

  const showArrows = canPrev || canNext;
  const showHeader = Boolean(title) || showViewAll || showArrows;
  const openAt = (index: number) => {
    onOpen?.();
    setActiveIndex(index);
    setModalOpen(true);
  };

  if (layout === 'auto' && assets.length === 1) {
    const [asset] = assets;
    const isVideo = asset.type === MediaType.Video;
    const isMediaFrame = singleFrame === 'media';
    return (
      <Box component="section" sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        {title && <SectionTitle as="h3">{title}</SectionTitle>}
        <SingleFrame
          $media={isMediaFrame}
          onClick={() => {
            openAt(0);
          }}
        >
          {asset.src && (
            <>
              <HeroGallerySlide asset={asset} muted={muted} paused={modalOpen} fit={isMediaFrame ? 'contain' : 'cover'} />
              {isVideo && (
                <SoundButton
                  muted={muted}
                  onClick={event => {
                    // The whole frame opens the lightbox on click — stop the
                    // toggle's click from bubbling into that handler.
                    event.stopPropagation();
                    setMuted(value => !value);
                  }}
                />
              )}
              {showFullscreen && (
                <FullscreenButton sx={fullscreenButtonSx}>
                  <AppIcon icon={FaFullscreenIcon} style={{fontSize: 24}} aria-hidden />
                </FullscreenButton>
              )}
            </>
          )}
        </SingleFrame>
        {modalOpen && (
          <GalleryModal
            assets={assets}
            index={activeIndex}
            title={lightboxTitle}
            onIndexChange={setActiveIndex}
            onClose={() => {
              setModalOpen(false);
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box component="section">
      {showHeader && (
        <Header>
          {title ? <SectionTitle as="h3">{title}</SectionTitle> : <span />}
          <HeaderEnd>
            {showViewAll && (
              <Button
                variant="link"
                onClick={() => {
                  openAt(0);
                }}
              >
                {t('view_all')}
              </Button>
            )}
            {showArrows && (
              <HeaderArrows>
                <NavArrow
                  direction="prev"
                  disabled={!canPrev}
                  onClick={() => {
                    scrollByDir(-1);
                  }}
                />
                <NavArrow
                  direction="next"
                  disabled={!canNext}
                  onClick={() => {
                    scrollByDir(1);
                  }}
                />
              </HeaderArrows>
            )}
          </HeaderEnd>
        </Header>
      )}

      <GalleryCarousel ref={ref} assets={assets} onOpen={openAt} />

      {modalOpen && (
        <GalleryModal
          assets={assets}
          index={activeIndex}
          title={lightboxTitle}
          onIndexChange={setActiveIndex}
          onClose={() => {
            setModalOpen(false);
          }}
        />
      )}
    </Box>
  );
}

MediaGallery.displayName = 'MediaGallery';
